import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpService } from 'src/app/core/data/service/http.service';
import { AuthenticationService } from 'src/app/core/modules/auth-core/data/service/authentication.service';
import { Raffle, RaffleAccessLevel } from '../schema/raffle';
import { throwError } from 'rxjs';
import { StripeAccountInfo } from '../schema/stripe-models';
import { BearerTicket } from '../schema/raffle-ticket';
import { Utilities } from 'src/app/core/helper/util';
import { SignalrEvent, SignalrService } from './signalr.service';
import { HubConnection } from '@microsoft/signalr';
import { AlertController } from '@ionic/angular';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { Log } from 'src/app/core/log';
import { RaffleAdmin } from '../schema/raffle-admin';

@Injectable({
  providedIn: 'root'
})
export class RaffleService extends HttpService {

  @Output() raffleUpdated = new EventEmitter<Raffle>();

  stripeAccountInfo: StripeAccountInfo;
  didJustSave = false;

  private serverRaffle: Raffle;
  private registeredRaffleId: number;
  private connection: HubConnection;
  private signalrSub: any;



  constructor(
    http: HttpClient,
    private alertCtrl: AlertController,
    private authService: AuthenticationService,
    private signalrService: SignalrService) {
    super(http);
    this.endpoint = `${this.apiURL}/raffles`;
  }

  get inRaffle() { return this.currentRaffle != null; };

  get originalRaffle() { return this.serverRaffle; }

  get currentRaffle() { return Raffle.currentRaffle; }
  set currentRaffle(raffle: Raffle) {

    if (raffle.raffleId !== this.currentRaffle?.raffleId) {
      // reset banking info
      this.stripeAccountInfo = null;
    }

    Raffle.currentRaffle = new Raffle(raffle);
    this.serverRaffle = new Raffle(raffle);
    this.raffleUpdated.emit(Raffle.currentRaffle);

    if (raffle.raffleId > 0 && raffle.raffleId !== this.registeredRaffleId) {
      this.registerWithHub();
    }
  }

  emitRaffleUpdate() {
    this.raffleUpdated.emit(Raffle.currentRaffle);
  }

  async getIsRaffleAdmin() {
    const accessLevel = await RaffleAdmin.fetchAccessLevel(Raffle.currentRaffle.raffleId);
    return (
      this.inRaffle === true &&
      ((accessLevel != null && accessLevel >= RaffleAccessLevel.RaffleAdmin) ||
        this.authService.profile.username.endsWith('@launch57.com'))
    );
  }

  async getIsRaffleCreator() {
    const accessLevel = await RaffleAdmin.fetchAccessLevel(Raffle.currentRaffle.raffleId);
    return (
      this.inRaffle === true &&
      ((accessLevel != null && accessLevel >= RaffleAccessLevel.RaffleCreator) ||
        this.authService.profile.username.endsWith('@launch57.com'))
    );
  }

  async generateRaffleCode(raffleName: string) {
    // take the raffle name, replace spaces with dashes
    // then replace double dashes with a single dash
    let baseCode = raffleName.toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/[^a-z0-9-]/g, '') // remove any non-alphanumeric or dash characters
      .substring(0, 20);

    if (baseCode.endsWith('-')) {
      baseCode = baseCode.substring(0, baseCode.length - 1);
    }

    let code = baseCode;
    while (!await this.accessCodeAvailable(code)) {
      // if we are taking on characters, then we need to limit length to 17
      if (baseCode.length > 17) {
        baseCode = baseCode.substring(0, 17);
        if (baseCode.endsWith('-')) {
          baseCode = baseCode.substring(0, baseCode.length - 1);
        }
      }

      code = `${baseCode}-${Utilities.getRandomString(2, true).toLowerCase()}`;
    }

    return code;
  }

  async accessCodeAvailable(code) {
    try {
      await this.checkAvailability(code);
      return true;
    } catch (error) {
      return false; // error means code is in use, not available
    }
  }

  revert() {
    Raffle.currentRaffle = new Raffle(this.serverRaffle);
  }

  hasChanges() {
    const curRaffleStr = JSON.stringify(Raffle.currentRaffle);
    const serverRaffleStr = JSON.stringify(this.serverRaffle);

    if (curRaffleStr === serverRaffleStr) {
      return false;
    }

    // log the differences
    // Log.debug('Current Raffle:', curRaffleStr);
    // Log.debug('Server Raffle:', serverRaffleStr);

    return true;
  }

  raffleEndDateChange() {
    return Raffle.currentRaffle.saleEnd !== this.serverRaffle.saleEnd;
  }

  async clearRaffleCache(raffleId?: number) {
    if (!this.authService.isSuper) { throwError('Not authorized'); }

    const ep = (raffleId > 0) ? `clearraffle/${raffleId}` : 'clearallraffles';
    return await super.fetch(`${this.endpoint}/${ep}`);
  }

  async loadRaffleByCode(code: string) {
    this.currentRaffle = await this.getRaffleByCode(code);

    // no need to await, but here we load stripe connect info for raffle so
    // we know if we should show banking requires attention or not
    if (this.currentRaffle.stripeAccountId?.trim().length > 0) {
      this.getRaffleStripeAccountInfo(this.currentRaffle.stripeAccountId, this.currentRaffle).then(() => {
        this.raffleUpdated.emit(this.currentRaffle);
      });
    }

    return this.currentRaffle;
  }

  async checkAvailability(code: string) {
    return await super.fetch(`${this.apiURL}/raffles/checkAvailability/${code}`);
  }

  async getRaffleByCode(code: string) {
    return new Raffle((await super.fetch(`${this.apiURL}/raffles/code/${code}`) as Raffle));
  }

  async getById(id: number) {
    this.currentRaffle = new Raffle(await super.getById(id));

    // no need to await, but here we load stripe connect info for raffle so
    // we know if we should show banking requires attention or not
    this.getRaffleStripeAccountInfo(this.currentRaffle.stripeAccountId, this.currentRaffle).then(() => {
      this.raffleUpdated.emit(this.currentRaffle);
    });

    return this.currentRaffle;
  }

  async getRaffleCopy(id: number) {
    return new Raffle((await super.fetch(`${this.apiURL}/raffles/getcopy/${id}`) as Raffle));
  }

  async fetchCreatedRaffles() {
    return (await super.fetch() as []).map(m => new Raffle(m));
  }

  async getBearerTickets(raffleId: number, endDate: string) {
    return ((await super.fetch(`${this.endpoint}/${raffleId}/ticketsForPrinting/${endDate}`)) as []).map(m => new BearerTicket(m));
  }

  async getReconciliationPackage() {
    return await super.fetch(`${this.endpoint}/${this.currentRaffle.raffleId}/reconcile`, 'arraybuffer') as any;
  }

  async getReportsPackage() {
    return await super.fetch(`${this.endpoint}/${this.currentRaffle.raffleId}/reports-package`, 'arraybuffer') as any;
  }

  async getExportUrl(type: string) {
    return await super.fetch(`${this.endpoint}/${this.currentRaffle.raffleId}/export/${type}`, 'text') as any;
  }

  async getPastRaffles(type: string, raffleIds: number[]) {
    return this.post(raffleIds, `${this.endpoint}/${this.currentRaffle.raffleId}/${type}`, 'text') as any;
  }

  async save(raffle: Raffle) {
    this.didJustSave = true;

    const fundraisingCampaignId = raffle.fundraisingCampaignId;

    const isNew = !(raffle.raffleId > 0);
    await this.currentRaffle.save(); // = new Raffle(await super.save(raffle, this.endpoint, 'json'));

    // if we created a raffle, then lets tell CBS about it
    if (isNew === true && this.currentRaffle.communityId > 0) {
      try {
        let url = `${AppSettings.cbsAPIUrl}/raffles/create/${this.currentRaffle.raffleId}`;
        if (fundraisingCampaignId > 0) {
          url += `/${fundraisingCampaignId}`;
        }
        const storedCbsToken = localStorage.getItem('ssoToken');
        await this.post(`"${storedCbsToken}"`, url);

        // reload the raffle to get the updated CBS info
        this.currentRaffle = new Raffle(await super.getById(this.currentRaffle.raffleId));

      } catch (error) {
        Log.debug('error calling CBS API', error);
      }
    } else {
      if (isNew === false) {
        // if we are not new, then we need to wait for the signalr event to be processed
        await new Promise(resolve => setTimeout(resolve, 250));
      }

      // if after delay (or is new) and there's a mismatch, then update the current raffle
      if (JSON.stringify(this.currentRaffle) !== JSON.stringify(this.serverRaffle)) {
        console.log('Server raffle is different from current raffle, updating current raffle.');
        this.currentRaffle = this.currentRaffle;
      }
    }

    return this.currentRaffle;
  }

  async sendTestEmail(email: string) {
    return this.post(null, `${this.endpoint}/${this.currentRaffle.raffleId}/test-email/${email}`);
  }

  async inviteUsers(raffleIds: number[]) {
    return this.post(raffleIds, `${this.endpoint}/${this.currentRaffle.raffleId}/invite`);
  }

  async getRaffleStripeAccountInfo(accountId: string, raffle: Raffle, forceRefresh: boolean = false) {
    // if we already have stripe account info, return it
    if (this.stripeAccountInfo != null && forceRefresh === false) {
      return this.stripeAccountInfo;
    }

    // load the stripe account info
    this.stripeAccountInfo = await StripeAccountInfo.load(raffle.raffleId, accountId);
    return this.stripeAccountInfo;
  }

  async disconnectFromSignalR() {
    // Unsubscribe from the events and close off the connection
    if (this.signalrSub != null) {
      this.signalrSub.unsubscribe();
      this.signalrSub = null;
    }
    if (this.connection != null && this.signalrService.isConnected) {
      await this.signalrService.disconnect();
      this.connection = null;
    }
  }


  protected isUpdate(entity: Raffle) {
    return (entity.raffleId > 0);
  }

  protected getUpdateUrl(entity: Raffle, endpoint: string = this.endpoint) {
    return `${endpoint}/${entity.raffleId}`;
  }


  private async registerWithHub() {
    // Okay, we ensured that we have an auction and aren't registered for it yet
    if (Raffle.currentRaffle != null && this.registeredRaffleId !== Raffle.currentRaffle.raffleId) {
      this.connection = await this.signalrService.initialize(async () => {
        // On connect..
        // Log.debug('connection to raffle hub established');

        if (this.registeredRaffleId > 0) {
          try {
            // unregister from the previous raffle
            await this.signalrService.runCommand('DisconnectFromHub', { purchaseGuid: `Raffle-${this.registeredRaffleId}` });
          } catch (error) {
            Log.debug('error disconnecting from raffle hub', error);
          }
        }

        // purchaseGuid is the property, it's a bit of a hack to work with the existing code
        this.registeredRaffleId = Raffle.currentRaffle.raffleId;
        this.signalrService.runCommand('RegisterWithHub', { purchaseGuid: `Raffle-${Raffle.currentRaffle.raffleId}` });
      }, () => {
        // On disconnect..
        Log.debug('connection to raffle hub closed');
      });

      if (this.signalrSub == null) {
        // listen for the purchase complete event
        this.signalrService.addEventHandler('RaffleUpdated');

        this.signalrSub = this.signalrService.eventReceived.subscribe((event: SignalrEvent) => {
          if (event.event === 'RaffleUpdated') {
            Log.debug(`${event.event} event received`, event.data);
            const raffle = new Raffle(event.data);
            if (this.didJustSave === false) {
              // show an alert here....
              this.alertCtrl.create({
                message: 'Another user has updated this raffle. Any unsaved changes will be lost.',
                header: 'Raffle Updated By Another User',
                buttons: ['OK']
              }).then(alert => alert.present());

            }
            this.currentRaffle = raffle;

            this.didJustSave = false;
          }
        });
      }
    }
  }

}
